import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useMemo, useRef, useState, type RefObject} from 'react'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {MilestoneIcon, PlusIcon} from '@primer/octicons-react'
import {MilestoneDescription} from './MilestoneDescription'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'

export type MilestonePickerMilestoneItem = {
  id: string
  title: string
  closed: boolean
  closedAt: string | null | undefined
  dueOn: string | null | undefined
  progressPercentage: number
  url: string
}

export type ItemPickerMilestoneProps = {
  repo: string
  owner: string
  activeMilestone: MilestonePickerMilestoneItem | null
  readonly?: boolean
  canCreateMilestone: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref: RefObject<HTMLButtonElement>) => React.ReactElement
  /**
   * Whether to render the milestone picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  shortcutHintVisible?: boolean
  noMilestoneItem?: ExtendedItemProps<MilestonePickerMilestoneItem>
  onSelectionChanged: (selectedItems: MilestonePickerMilestoneItem[]) => void
  showMilestoneDescription?: boolean
  title?: string
  showNoMatchItem?: boolean
  repositoryId: string

  milestones: MilestonePickerMilestoneItem[]
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  fetchMilestones: (options: {
    owner: string
    repo: string
    query: string
    count?: number
  }) => Promise<MilestonePickerMilestoneItem[]>
  onCreate: CreateMilestoneMutationCallback
}

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateLabelVariables = {repositoryId: string; title: string}
export type CreateMilestoneMutationCallback = MutationCallback<CreateLabelVariables, MilestonePickerMilestoneItem>

export function ItemPickerMilestone({
  repo,
  owner,
  milestones,
  repositoryId,
  activeMilestone,
  onSelectionChanged,
  insidePortal,
  anchorElement,
  nested,
  triggerOpen,
  initialFilter,
  isLoading,
  setIsLoading,
  noMilestoneItem,
  showMilestoneDescription,
  title,
  canCreateMilestone,
  fetchMilestones,
  onCreate,
  showNoMatchItem = false,
}: ItemPickerMilestoneProps) {
  const [filter, setFilter] = useState('')
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [searchedMilestones, setSearchedMilestones] = useState<MilestonePickerMilestoneItem[] | undefined>()
  const [loadedMilestones, setLoadedMilestones] = useState<Array<MilestonePickerMilestoneItem & {__isNew__?: boolean}>>(
    [],
  )
  const {addToast} = useToastContext()

  const items = useMemo(() => {
    if (searchedMilestones !== undefined && searchedMilestones.length > 0) return searchedMilestones

    const repositoryMilestones = milestones
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title))
      .concat(loadedMilestones)

    if (noMilestoneItem) repositoryMilestones.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestoneItem)
    if (!filter) return repositoryMilestones
    return repositoryMilestones.filter(l => l.title.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [milestones, filter, loadedMilestones, noMilestoneItem, searchedMilestones])

  const isNoMilestoneSelected = noMilestoneItem && noMilestoneItem.selected

  const openItems = useMemo(() => {
    if (noMilestoneItem) {
      return items.filter(m => m.closed === false)
    } else {
      return []
    }
  }, [noMilestoneItem, items])

  const searchMilestones = useCallback(
    async (query: string) => {
      setIsServerFetching(true)
      setFilter(query)

      try {
        const fetchedData = await fetchMilestones({query, owner, repo, count: 10})

        if (fetchedData !== undefined) {
          if (noMilestoneItem && hasFuzzyMatch(query, noMilestoneItem.source?.title ?? '')) {
            fetchedData.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestoneItem)
          }

          setSearchedMilestones(fetchedData)
        }
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchMilestones,
        })
      } finally {
        setIsServerFetching(false)
      }
    },
    [addToast, fetchMilestones, noMilestoneItem, owner, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => searchMilestones(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    async (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) {
        return
      }

      if (trimmedFilter === '') {
        setSearchedMilestones(undefined)
        setFilter(trimmedFilter)
        return
      }

      debounceFetchSearchData(trimmedFilter)
    },
    [debounceFetchSearchData, filter],
  )

  const getItemKey = useCallback((milestone: MilestonePickerMilestoneItem) => milestone.id, [])

  const createNewMilestone = useCallback(
    (
      _: ItemProps,
      milestone: MilestonePickerMilestoneItem,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          title: milestone.title,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateMilestone,
            })
            setIsLoading(false)
          },
          onSuccess: newMilestone => {
            if (newMilestone) {
              setLoadedMilestones([{...newMilestone, __isNew__: true}, ...loadedMilestones])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedMilestones, onCreate, repositoryId, setIsLoading],
  )

  const convertToItemProps = useCallback(
    (milestone: MilestonePickerMilestoneItem): ExtendedItemProps<MilestonePickerMilestoneItem> => {
      if (noMilestoneItem && milestone.id === SPECIAL_VALUES.noMilestoneData.id) {
        return {...noMilestoneItem}
      }

      if (milestone.id === filter) {
        return {
          id: milestone.id,
          text: `Create new milestone: "${milestone.title}"`,
          source: milestone,
          trailingVisual: PlusIcon,
          sx: {wordBreak: 'break-word', '& [class*=BaseVisualContainer]:first-child': {display: 'none'}},
          onAction: (item: ItemProps, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
            createNewMilestone(item, milestone, event),
        }
      }

      const {closed, closedAt, progressPercentage, dueOn} = milestone

      return {
        id: milestone.id,
        text: milestone.title,
        description: showMilestoneDescription
          ? ((
              <MilestoneDescription
                closed={closed}
                closedAt={closedAt}
                progressPercentage={progressPercentage}
                dueOn={dueOn}
                showProgressPercentage={false}
              />
            ) as unknown as string)
          : undefined,
        descriptionVariant: 'block',
        sx: {wordBreak: 'break-word'},
        source: milestone,
        groupId: milestone.closed ? closedGroup.groupId : openGroup.groupId,
        leadingVisual: () => <MilestoneIcon size={16} />,
      }
    },
    [createNewMilestone, filter, noMilestoneItem, showMilestoneDescription],
  )

  // Only show groups for which the filter returns at least one item
  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (items.some(i => !i.closed)) {
      itemGroups.push(openGroup)
    }
    if (items.some(i => i.closed)) {
      itemGroups.push(closedGroup)
    }
    return itemGroups
  }, [items])

  if (noMilestoneItem && isNoMilestoneSelected) {
    activeMilestone = SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestoneItem
  }

  const anchorRef = useRef<HTMLButtonElement>(null)
  const initialSelectedItems = useMemo(() => {
    return activeMilestone ? [activeMilestone] : []
  }, [activeMilestone])

  const showNoMatchMessage = useMemo<boolean>(() => {
    const hasNoItems = !isServerFetching && !filter && items.length === 0
    const hasNoFilterResults = !showNoMatchItem && !!filter && items.length === 0
    return hasNoItems || hasNoFilterResults
  }, [filter, isServerFetching, items.length, showNoMatchItem])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  return (
    <ItemPicker
      loading={isLoading || isServerFetching}
      items={noMilestoneItem ? openItems : items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={title ?? LABELS.milestonesHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter milestones ${
        milestones.length >= VALUES.milestonesMaxPreloadCount
          ? `(${VALUES.milestonesMaxPreloadCount} milestones loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={onSelectionChanged}
      groups={noMilestoneItem ? undefined : groups}
      renderAnchor={props => anchorElement(props, anchorRef)}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Milestone results'}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
      selectPanelRef={anchorRef}
      keybindingCommandId="item-pickers:open-milestone"
      customNoMatchItem={
        canCreateMilestone && showNoMatchItem && filter
          ? ({
              id: filter,
              closed: false,
              progressPercentage: 0,
              title: filter,
              url: '',
            } as MilestonePickerMilestoneItem)
          : undefined
      }
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No milestones were found',
              body: 'Try searching with a different query for results.',
              variant: 'empty',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}

try{ ItemPickerMilestone.displayName ||= 'ItemPickerMilestone' } catch {}